import { useState, useCallback, useEffect, useRef } from 'react';
import { fetchAdherence } from './flowPlannerApi';
import { responseHasActuals } from './flowPlannerAdherenceModel';

const POLL_INTERVAL_MS = 60 * 1000;

export default function useFlowPlannerActuals(date, planId) {
  const [unlocked, setUnlocked] = useState(false);
  const [visible, setVisible] = useState(false);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const inputsRef = useRef('');
  const openRef = useRef(false);
  const generationRef = useRef(0);
  inputsRef.current = `${date}|${planId}`;
  openRef.current = unlocked && visible;

  const doFetch = useCallback(
    async (isRefresh) => {
      const inputs = `${date}|${planId}`;
      const generation = generationRef.current;
      const isCurrent = () => inputsRef.current === inputs && generationRef.current === generation;
      if (isRefresh) setRefreshing(true);
      else {
        setLoading(true);
        setError(null);
      }
      try {
        const resp = await fetchAdherence(date, planId);
        if (!isCurrent()) return;
        if (isRefresh) {
          if (responseHasActuals(resp)) {
            setResponse(resp);
            setError(null);
          }
        } else {
          setResponse(resp || null);
          if (!responseHasActuals(resp)) setError('No actuals are available for this plan yet.');
        }
      } catch (e) {
        if (!isCurrent()) return;
        if (!isRefresh) setError('Could not load actuals. Planned values are unaffected.');
      } finally {
        if (isCurrent()) {
          if (isRefresh) setRefreshing(false);
          else setLoading(false);
        }
      }
    },
    [date, planId]
  );

  useEffect(() => {
    setResponse(null);
    setError(null);
    setLoading(false);
    setRefreshing(false);
    if (openRef.current) doFetch(false);
  }, [date, planId, doFetch]);

  const unlock = useCallback(() => {
    setUnlocked(true);
    setVisible(true);
    if (!response && !loading) doFetch(false);
  }, [response, loading, doFetch]);

  const toggle = useCallback(() => {
    const next = !visible;
    setVisible(next);
    if (!next || loading || refreshing) return;
    doFetch(!!response);
  }, [visible, response, loading, refreshing, doFetch]);

  useEffect(() => {
    if (!unlocked || !visible) return undefined;
    const id = setInterval(() => doFetch(true), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [unlocked, visible, doFetch]);

  const dismissError = useCallback(() => {
    setError(null);
    if (!responseHasActuals(response)) {
      generationRef.current += 1;
      setUnlocked(false);
      setVisible(false);
      setResponse(null);
      setLoading(false);
      setRefreshing(false);
    }
  }, [response]);

  return {
    unlocked,
    showActuals: unlocked && visible,
    response,
    loading,
    refreshing,
    error,
    unlock,
    toggle,
    dismissError
  };
}
